import React, { useCallback, useEffect, useMemo } from 'react';
import { Form, ReduxControls, ProgressIndicator, ErrorBanner } from 'rhombus-react';
import {
	CREATIVE_ASSET_TYPES
} from 'utils/constants';
import {
	IMPOSIUM_COMP_CREATION_METHOD,
	IMPOSIUM_PROJECT_CREATION_METHOD
} from 'widgets/social2/constants/common';
import { useDispatch, useSelector } from 'react-redux';
import { getFormValues, Field, change as changeFormValue } from "redux-form";
import { noop } from "utils/common";
import { getImposiumCompositions } from "reducers/ImposiumCompositions";
import InfoBanner from "widgets/InfoBanner";

import './ImposiumCompSelector.css';

export default function ImposiumCompSelector({
	input,
	meta,
	customTip,
	showProjectName = true,
	placeholder = "Select a composition to be used for this creative",
	...props
}) {
	const dispatch = useDispatch();
	const inputProps = {
		...input,
		...props,
		meta,
	};

	const { name, onChange } = input || {};
	const [adType] = name.split(".");
	const {
		doVersionsExist,
		creativeValues,
		libraryId,
		compositions,
		isLoadingCompositions,
		errorObj,
	} = useSelector((state) => {
		return {
			doVersionsExist: state.creativeDetails?.numberOfVersions > 1,
			creativeValues: getFormValues(meta.form)(state)?.[adType] || {},
			libraryId: state.libraryProperties?.id,
			compositions: state.imposiumCompositions?.items,
			isLoadingCompositions: state.imposiumCompositions?.isLoading,
			errorObj: state.imposiumCompositions?.error || {},
		};
	});

	const { disabled } = inputProps;
	const isRadioDisabled = disabled || doVersionsExist;
	const isCreativeDynamic = creativeValues?.isDynamic;
	const assetType = creativeValues?.assetType;

	//set defaults
	const value = input.value?.creationMethod ? input.value : {
		creationMethod: IMPOSIUM_COMP_CREATION_METHOD.NEW_COMPOSITION,
	};

	const compositionId = value?.id;

	const project = creativeValues.selectedStoryId;
	const projectState = creativeValues.imposiumProject || creativeValues.cards && creativeValues.cards[0]?.imposiumProject;
	const isExistingProject = projectState?.creationMethod === IMPOSIUM_PROJECT_CREATION_METHOD.EXISTING_PROJECT;
	const projectId = isExistingProject ? project?.value : undefined;
	const projectName = isExistingProject ? project?.label : project?.newProjectName;

	const onCreationMethodChange = useCallback((e) => {
		const creationMethod = e.target.defaultValue;
		if (creationMethod === IMPOSIUM_COMP_CREATION_METHOD.NEW_COMPOSITION){
			dispatch(changeFormValue(meta.form, `${adType}.selectedCompositionId`, null));
			dispatch(changeFormValue(meta.form, `${adType}.imposiumComposition.id`, null));
		}
		onChange({
			...value,
			creationMethod,
		});
	}, [disabled, onChange, value]);

	const onCompIdChange = useCallback((newValue) => {
		onChange({
			...value,
			id: newValue?.value,
			name: newValue?.label,
		});
	}, [disabled, onChange, value]);

	useEffect(() => {
		if (projectId && compositions?.length === 0 && value.creationMethod === IMPOSIUM_COMP_CREATION_METHOD.EXISTING_COMPOSITION) {
			dispatch(getImposiumCompositions(libraryId, projectId, null));
		}
	}, [libraryId, projectId, value.creationMethod]);

	useEffect(() => {
		if (compositions?.length > 0 && compositionId) {
			//find composition name and set value for dropdown
			const currentComposition = compositions.find(comp => comp.id === compositionId);
			if (currentComposition) {
				let formField = input.name
					.replace('imposiumComposition', 'selectedCompositionId')
					.replace(/\.cards\[\d+\]/, '');//cards reuse main field
				dispatch(changeFormValue(meta.form, formField, {
					value: currentComposition.id,
					label: currentComposition.name,
				}));
			}
		}
	}, [compositions, creativeValues.currentCard, creativeValues._currentActivePAC]);

	const compositionOptions = useMemo(() => {
		return compositions?.map(comp => ({
			value: comp.id,
			label: comp.name,
		}));
	}, [compositions]);

	if (!isCreativeDynamic || assetType !== CREATIVE_ASSET_TYPES.IMPOSIUM) {
		return null;
	}

	if (!isExistingProject) {
		return (
			<div className="full-width">
				<div className="full-width imposium-comp-selector">
					<InfoBanner>
						Imposium will automatically generate the corresponding composition for this ad.
					</InfoBanner>
				</div>
			</div>
		);
	}

	return (
		<div className="full-width">
			<div className="full-width imposium-comp-selector">
				<ProgressIndicator isLoading={isLoadingCompositions} />
				{!!Object.keys(errorObj)?.length && (
					<ErrorBanner
						title="Sorry, please fix the errors below and try again."
						messages={[errorObj.description]}
					/>
				)}
				<div className="full-width title">
					Select Composition
				</div>
				<div className="full-width radio-box">
					<div className="ex-comp-radio">
						<Form.Checkbox
							label="From the Imposium Project"
							type="radio"
							disabled={isRadioDisabled}
							value={IMPOSIUM_COMP_CREATION_METHOD.EXISTING_COMPOSITION}
							onChange={onCreationMethodChange}
							checked={value.creationMethod === IMPOSIUM_COMP_CREATION_METHOD.EXISTING_COMPOSITION}
						/>
					</div>
					<div className="new-comp-radio">
						<Form.Checkbox
							label="Create a new composition"
							type="radio"
							disabled={isRadioDisabled}
							value={IMPOSIUM_COMP_CREATION_METHOD.NEW_COMPOSITION}
							onChange={onCreationMethodChange}
							checked={value.creationMethod === IMPOSIUM_COMP_CREATION_METHOD.NEW_COMPOSITION}
						/>
					</div>
				</div>
				<div className="full-width comp-selector">
					{value.creationMethod === IMPOSIUM_COMP_CREATION_METHOD.EXISTING_COMPOSITION && (
						<Field
							name="selectedCompositionId"
							placeholder={placeholder}
							component={ReduxControls.Select}
							isLoading={isLoadingCompositions}
							isClearable={false}
							options={compositionOptions}
							disabled={isRadioDisabled}
							onChange={onCompIdChange}
							value={value.id}
							onMenuScrollToBottom={noop}
						/>
					)
					}
					{showProjectName && (
						<div className="selected-project"><b>Imposium
							Project:</b> {projectName}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
ImposiumCompSelector.displayName = "ImposiumCompSelector";

export const imposiumComposition = {
	name: "imposiumComposition",
	required: true,
	component: ImposiumCompSelector,
};

export const imposiumCompositionWithoutProjectName = {
	...imposiumComposition,
	showProjectName: false,
};

export const imposiumCompositionForCard = {
	...imposiumComposition,
	placeholder: "Select a composition to be used for this card",
};
